import axios, { AxiosError, AxiosResponse } from "axios";

import {
  ICustomFieldColumn,
  ICustomFieldFormItem,
  ICustomFieldValueInput,
  IPatientCustomFieldValue,
} from "../@types/CustomField";
import { api } from "./api";

export const customFieldsService = {
  // EN-5067: the union of enabled add-patient fields across the given wards, deduplicated and
  // already ordered by displayOrder. wardIds are passed as repeated query params.
  async getCustomFieldsForWards(
    wardIds: number[]
  ): Promise<AxiosResponse<ICustomFieldFormItem[]>> {
    const query = wardIds.map((id) => `wardIds=${id}`).join("&");
    const url = `/Portal/CustomFields/ForWards?${query}`;

    const serverResponse = await api.get<ICustomFieldFormItem[]>(url);

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  // EN-5067: the patient's saved custom-field answers marked to show on the side panel.
  async getPatientCustomFields(
    patientId: number
  ): Promise<AxiosResponse<IPatientCustomFieldValue[]>> {
    const url = `/Portal/CustomFields/Patients/${patientId}`;

    const serverResponse = await api.get<IPatientCustomFieldValue[]>(url);

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  // EN-5067: partial-upsert a patient's side-panel custom-field values. Send only changed fields;
  // an empty value clears an optional field. 204 on success; 400/404 return an ErrorResponse body
  // (unwrapped here so the caller can read errors).
  async setPatientCustomFields(
    patientId: number,
    payload: { customFields: ICustomFieldValueInput[] }
  ): Promise<AxiosResponse | undefined> {
    const url = `/Portal/CustomFields/Patients/${patientId}`;

    const serverResponse = await api
      .put(url, payload)
      .then((response: AxiosResponse | AxiosError) => {
        if (axios.isAxiosError(response)) {
          return response.response;
        }
        return response;
      });

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  // EN-5067: the custom fields available as optional patient-list columns (union across the
  // staff's accessible wards), ordered by displayOrder.
  async getPatientListColumns(): Promise<AxiosResponse<ICustomFieldColumn[]>> {
    const url = "/Portal/CustomFields/PatientListColumns";

    const serverResponse = await api.get<ICustomFieldColumn[]>(url);

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },
};
